import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { validateOTDateForDutyRosterOptimized } from '@/services/dutyRosterService';

/**
 * Hook to fetch duty roster employee data and validate OT date
 * Returns employee duty roster status, group info, and date validation
 */
export function useDutyRosterEmployeeData(employeeId: string | null, otDate?: string) {
  return useQuery({
    queryKey: ['duty-roster-employee', employeeId, otDate],
    queryFn: async () => {
      if (!employeeId || !otDate) return null;
      
      // First check if employee is duty roster
      const { data: employee, error: empError } = await supabase
        .from('employees')
        .select('is_duty_roster, group_id')
        .eq('id', employeeId)
        .single();

      if (empError || !employee) {
        return {
          isDutyRoster: false,
          groupInfo: null,
          dateValidation: null,
          error: 'Employee not found'
        };
      }

      // If not duty roster, return basic info
      if (!employee.is_duty_roster) {
        return {
          isDutyRoster: false,
          groupInfo: null,
          dateValidation: null,
          error: null
        };
      }

      // If duty roster but no group assigned
      if (!employee.group_id) {
        return {
          isDutyRoster: true,
          groupInfo: null,
          dateValidation: null,
          error: 'Employee is duty roster but not assigned to any group'
        };
      }

      // Fetch group information separately
      // Note: This could be optimized further by using prefetched groups data
      const { data: group, error: groupError } = await supabase
        .from('emp_groups')
        .select('group_name, residency_type')
        .eq('id', employee.group_id)
        .single();

      if (groupError || !group) {
        return {
          isDutyRoster: true,
          groupInfo: null,
          dateValidation: null,
          error: 'Failed to fetch group information'
        };
      }

      // Validate the OT date for duty roster employee using optimized function
      const dateValidation = await validateOTDateForDutyRosterOptimized(employeeId, otDate);

      return {
        isDutyRoster: true,
        groupInfo: {
          groupId: employee.group_id,
          groupName: group.group_name || 'Unknown Group',
          residencyType: group.residency_type || 'Unknown'
        },
        dateValidation,
        error: null
      };
    },
    enabled: !!employeeId && !!otDate,
    staleTime: 5 * 60 * 1000, // 5 minutes cache - increased for better performance
    gcTime: 15 * 60 * 1000,   // 15 minutes garbage collection
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
    refetchOnMount: false,       // Use cached data when available
    retry: 1,
    retryDelay: 1000 // 1 second delay between retries
  });
}

/**
 * Hook to get employee duty roster status only (without date validation)
 */
export function useEmployeeDutyRosterStatus(employeeId: string | null) {
  return useQuery({
    queryKey: ['employee-duty-roster-status', employeeId],
    queryFn: async () => {
      if (!employeeId) return null;
      
      const { data: employee, error } = await supabase
        .from('employees')
        .select('is_duty_roster, group_id')
        .eq('id', employeeId)
        .single();

      if (error || !employee) {
        return {
          isDutyRoster: false,
          groupInfo: null,
          error: 'Employee not found'
        };
      }

      // If duty roster employee, fetch group info
      if (employee.is_duty_roster && employee.group_id) {
        const { data: group, error: groupError } = await supabase
          .from('emp_groups')
          .select('group_name, residency_type')
          .eq('id', employee.group_id)
          .single();

        if (groupError || !group) {
          return {
            isDutyRoster: true,
            groupInfo: null,
            error: 'Failed to fetch group information'
          };
        }

        return {
          isDutyRoster: true,
          groupInfo: {
            groupId: employee.group_id,
            groupName: group.group_name || 'Unknown Group',
            residencyType: group.residency_type || 'Unknown'
          },
          error: null
        };
      }

      return {
        isDutyRoster: employee.is_duty_roster || false,
        groupInfo: null,
        error: null
      };
    },
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    retry: 1
  });
}
